import { useState, useMemo, useCallback, useEffect, ReactNode } from "react";
import { Box, Badge, Typography, IconButton, Tooltip } from "@mui/material";
import { RestoreFromTrash as RestoreFromTrashIcon, Restore as RestoreIcon } from "@mui/icons-material";
import {
  TextField,
  DateField,
  NumberField,
  BooleanField,
  FunctionField,
  ReferenceField,
  useGetList,
  useDataProvider,
  useNotify,
  useRefresh,
  useUnselectAll,
  useListContext,
  useList,
  ListContextProvider,
} from "react-admin";
import { PageHeader } from "@/components/common/PageHeader";
import { ResourceName, ValidAny } from "@/types";
import { getResourceIcon } from "@/utils/icons";
import { PaginatedDatagrid } from "@/components/ui/react-admin/PaginatedDatagrid";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { ResourceColumnConfig, RESOURCE_COLUMNS, DELETED_RECORD_COLUMNS } from "@/constants/resourceColumns";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { StyledTabs, StyledTab } from "@/components/ui/mui/StyledTabs";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { ConfirmLabelType } from "@/types/shared/uiComponents";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

// ─── useResourceColumns Hook ──────────────────────────────────────────────────

interface UseResourceColumnsOptions {
  omit?: string[];
  append?: ResourceColumnConfig[];
  prepend?: ResourceColumnConfig[];
  only?: string[];
}

const useResourceColumns = (
  resource: ResourceName,
  options: UseResourceColumnsOptions = {}
): ResourceColumnConfig[] => {
  const { omit = [], append = [], prepend = [], only } = options;

  return useMemo(() => {
    let columns = RESOURCE_COLUMNS[resource] ?? [];

    if (only && only.length > 0) {
      columns = columns.filter((col) => only.includes(col.source));
    }

    if (omit.length > 0) {
      columns = columns.filter((col) => !omit.includes(col.source));
    }

    return [...prepend, ...columns, ...append];
  }, [resource, omit, append, prepend, only]);
};

// ─── renderColumn Utility ─────────────────────────────────────────────────────

const renderColumn = (config: ResourceColumnConfig): ReactNode => {
  const { source, label, type, sortable = true, width, textAlign } = config;
  const commonProps = {
    key: source,
    source,
    label,
    sortable,
    sx: { width, textAlign },
  };

  switch (type) {
    case "text":
      return <TextField {...commonProps} />;
    case "date":
      return <DateField {...commonProps} showTime />;
    case "number":
      return <NumberField {...commonProps} options={config.numberOptions} />;
    case "currency":
      return <NumberField {...commonProps} options={config.numberOptions ?? { style: "currency", currency: "USD" }} />;
    case "boolean":
      return <BooleanField {...commonProps} />;
    case "chip":
      return (
        <FunctionField
          {...commonProps}
          render={(record: ValidAny) => {
            const value = record?.[source];
            if (!value) return null;
            return <StatusChip label={formatToTitleCase(value)} color={value} />;
          }}
        />
      );
    case "reference":
      if (!config.reference || !config.referenceDisplay) {
        return <TextField {...commonProps} />;
      }
      return (
        <ReferenceField
          key={source}
          source={source}
          reference={config.reference}
          label={label}
          sortable={sortable}
          link={config.referenceLink === "show" ? "show" : false}
        >
          <TextField source={config.referenceDisplay} />
        </ReferenceField>
      );
    case "function":
      return config.render ? <FunctionField {...commonProps} render={config.render} /> : null;
    default:
      return <TextField {...commonProps} />;
  }
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface RecoveryTab {
  key: string;
  label: string;
  resource: ResourceName;
  countField: string;
}

// ─── Tab Configuration ────────────────────────────────────────────────────────

const RECOVERY_TABS: RecoveryTab[] = [
  { key: "stoploss-claims", label: "Claims", resource: ResourceName.STOPLOSS_CLAIMS, countField: "stoploss_claims" },
  { key: "claim-items", label: "Items", resource: ResourceName.CLAIM_ITEMS, countField: "claim_items" },
  { key: "policies", label: "Policies", resource: ResourceName.POLICIES, countField: "policies" },
  { key: "claimants", label: "Claimants", resource: ResourceName.CLAIMANTS, countField: "claimants" },
  { key: "clients", label: "Clients", resource: ResourceName.CLIENTS, countField: "clients" },
  { key: "vendors", label: "Vendors", resource: ResourceName.VENDORS, countField: "vendors" },
  { key: "tasks", label: "Tasks", resource: ResourceName.TASKS, countField: "tasks" },
  { key: "documents", label: "Documents", resource: ResourceName.DOCUMENTS, countField: "documents" },
  { key: "reimbursements", label: "Reimb.", resource: ResourceName.REIMBURSEMENTS, countField: "reimbursements" },
  { key: "endorsements", label: "Endorsements", resource: ResourceName.ENDORSEMENTS, countField: "endorsements" },
];

// ─── Deleted Counts Hook ──────────────────────────────────────────────────────

type DeletedCounts = Record<string, number>;

const useDeletedCounts = () => {
  const dataProvider = useDataProvider();
  const [counts, setCounts] = useState<DeletedCounts>({});
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    dataProvider
      .getDeletedCounts()
      .then(({ data }: { data: DeletedCounts }) => setCounts(data))
      .catch(() => setCounts({}));
  }, [dataProvider, version]);

  return { counts, refresh };
};

// ─── Row Restore Action ───────────────────────────────────────────────────────

const RestoreRowButton = ({
  record,
  resource,
  onRestored,
}: {
  record: ValidAny;
  resource: ResourceName;
  onRestored: () => void;
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();

  const handleRestore = async () => {
    setConfirmOpen(false);
    try {
      await dataProvider.restore(resource, { id: record.id });
      notify("Record restored successfully", { type: "success" });
      onRestored();
    } catch (error) {
      notifyError(error);
    }
  };

  return (
    <>
      <Tooltip title="Restore">
        <IconButton size="small" color="primary" onClick={() => setConfirmOpen(true)}>
          <RestoreIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <ConfirmationDialog
        open={confirmOpen}
        title="Restore Record"
        message={`Are you sure you want to restore this ${formatToTitleCase(resource).toLowerCase()} record?`}
        confirmLabel={ConfirmLabelType.Confirm}
        onConfirm={handleRestore}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

// ─── Bulk Restore Actions ─────────────────────────────────────────────────────

const RecoveryBulkActions = ({ resource, onRestored }: { resource: ResourceName; onRestored: () => void }) => {
  const { selectedIds } = useListContext();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const unselectAll = useUnselectAll(resource);

  const handleBulkRestore = async () => {
    setConfirmOpen(false);
    try {
      await dataProvider.restoreMany(resource, { ids: selectedIds });
      notify(`${selectedIds.length} record(s) restored successfully`, { type: "success" });
      unselectAll();
      onRestored();
    } catch (error) {
      notifyError(error);
    }
  };

  if (!selectedIds || selectedIds.length === 0) return null;

  return (
    <>
      <Tooltip title={`Restore ${selectedIds.length} selected`}>
        <IconButton color="primary" onClick={() => setConfirmOpen(true)}>
          <RestoreIcon />
        </IconButton>
      </Tooltip>
      <ConfirmationDialog
        open={confirmOpen}
        title="Restore Records"
        message={`Are you sure you want to restore ${selectedIds.length} selected record(s)?`}
        confirmLabel={ConfirmLabelType.Confirm}
        onConfirm={handleBulkRestore}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

// ─── Deleted Records List ─────────────────────────────────────────────────────

const RECOVERY_OMIT = ["status", "claim_status", "client_status", "has_active_policy"];

const DeletedRecordsList = ({ tab, onRestored }: { tab: RecoveryTab; onRestored: () => void }) => {
  const refresh = useRefresh();

  const { data, isLoading } = useGetList(tab.resource, {
    sort: { field: "deleted_datetime", order: "DESC" },
    pagination: { page: 1, perPage: 1000 },
    meta: { includeDeleted: true },
  });

  const columns = useResourceColumns(tab.resource, {
    omit: RECOVERY_OMIT,
    append: DELETED_RECORD_COLUMNS,
  });

  const handleRestored = useCallback(() => {
    refresh();
    onRestored();
  }, [refresh, onRestored]);

  const restoreColumn: ResourceColumnConfig = {
    source: "_restore",
    label: "",
    type: "function",
    sortable: false,
    width: 50,
    render: (record: ValidAny) => (
      <RestoreRowButton record={record} resource={tab.resource} onRestored={handleRestored} />
    ),
  };

  const allColumns = [...columns, restoreColumn];

  const emptyMessage = (
    <Box sx={{ py: 6, textAlign: "center" }}>
      <Typography variant="body1" color="text.secondary">
        No deleted {tab.label.toLowerCase()} found.
      </Typography>
    </Box>
  );

  const listContext = useList({
    data: data ?? [],
    isLoading,
    sort: { field: "deleted_datetime", order: "DESC" },
    resource: tab.resource,
  });

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 2 }}>
      <ListContextProvider value={listContext}>
        <PaginatedDatagrid
          data={data ?? []}
          isLoading={isLoading}
          resource={tab.resource}
          sort={{ field: "deleted_datetime", order: "DESC" }}
          initialPerPage={25}
          rowClick={false}
          bulkActionButtons={<RecoveryBulkActions resource={tab.resource} onRestored={handleRestored} />}
          empty={emptyMessage}
        >
          {allColumns.map(renderColumn)}
        </PaginatedDatagrid>
      </ListContextProvider>
    </Box>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const RecordRecovery = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { counts, refresh: refreshCounts } = useDeletedCounts();

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const currentTab = RECOVERY_TABS[activeTab];

  return (
    <Box>
      <PageHeader
        customTitle="Record Recovery"
        hideSectionTitle
        headerIcon={<RestoreFromTrashIcon sx={{ color: "primary.main" }} />}
      />

      <Box sx={{ px: { xs: 2, md: 4 }, pt: 1 }}>
        <StyledTabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          {RECOVERY_TABS.map((tab) => {
            const count = counts[tab.countField] ?? 0;
            const Icon = getResourceIcon(tab.resource);
            return (
              <StyledTab
                key={tab.key}
                icon={
                  <Badge badgeContent={count} color="error" max={999} sx={{ "& .MuiBadge-badge": { fontSize: 10 } }}>
                    <Icon fontSize="small" />
                  </Badge>
                }
                iconPosition="start"
                label={tab.label}
                sx={{ opacity: count === 0 ? 0.5 : 1, minHeight: 48 }}
              />
            );
          })}
        </StyledTabs>
      </Box>

      <DeletedRecordsList key={currentTab.key} tab={currentTab} onRestored={refreshCounts} />
    </Box>
  );
};

export default RecordRecovery;
